import { Controller, Control } from 'react-hook-form';
import { Box, FormLabel, FormHelperText, useTheme } from '@mui/material';
import Editor from 'react-simple-code-editor';
import { useEffect, useState } from 'react';
import './styles.css';

interface ControlledCodeEditorProps {
  control: Control<any, any>;
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  errorMessage?: string;
  rows?: number;
  fullWidth?: boolean;
}

export const ControlledCodeEditor = ({
  control,
  name,
  label,
  placeholder,
  helperText,
  required,
  errorMessage,
  rows = 8,
  fullWidth = true,
}: ControlledCodeEditorProps) => {
  const theme = useTheme();
  const [prismLoaded, setPrismLoaded] = useState(false);

  useEffect(() => {
    // Cargar PrismJS dinámicamente en useEffect como sugiere la solución
    const loadPrism = async () => {
      try {
        // Importar Prism core
        const Prism = await import('prismjs');
        
        // Cargar dependencias en el orden correcto usando require
        require('prismjs/themes/prism.css');
        require('prismjs/components/prism-markup');
        require('prismjs/components/prism-css');
        require('prismjs/components/prism-clike');
        require('prismjs/components/prism-javascript');
        require('prismjs/components/prism-markdown');
        
        // Guardar Prism completo en window
        (window as any).Prism = Prism.default || Prism;
        
        setPrismLoaded(true);
      } catch (error) {
        console.warn('Error loading PrismJS:', error);
        setPrismLoaded(false);
      }
    };

    loadPrism();
  }, []);

  const highlightCode = (code: string) => {
    if (!prismLoaded || !(window as any).Prism) {
      return code; // Fallback a texto plano
    }

    try {
      const Prism = (window as any).Prism;
      
      // Verificar que el lenguaje esté disponible
      if (Prism.languages && Prism.languages.markdown) {
        return Prism.highlight(code, Prism.languages.markdown, 'markdown');
      } else if (Prism.languages && Prism.languages.markup) {
        return Prism.highlight(code, Prism.languages.markup, 'markup');
      } else {
        console.warn('No markdown or markup language found in Prism');
        return code;
      }
    } catch (error) {
      console.warn('Error highlighting code:', error);
      return code;
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
          {label && (
            <FormLabel 
              component="legend" 
              required={required}
              sx={{ 
                mb: 1, 
                fontSize: '0.875rem',
                color: error ? theme.palette.error.main : theme.palette.text.primary,
              }}
            >
              {label}
            </FormLabel>
          )}
          
          <Box
            sx={{
              border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
              borderRadius: 0,
              px: 2,
              py: 1,
              overflow: 'hidden',
              '&:hover': {
                borderColor: error ? theme.palette.error.main : theme.palette.action.hover,
              },
              '&:focus-within': {
                borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
                borderWidth: 2,
                boxShadow: error 
                  ? `0 0 0 1px ${theme.palette.error.main}` 
                  : `0 0 0 1px ${theme.palette.primary.main}`,
              },
              '& .code-editor-container': {
                position: 'relative',
              },
            }}
          >
            <Editor
              value={value || ''}
              onValueChange={onChange}
              highlight={highlightCode}
              padding={12}
              placeholder={placeholder}
              style={{
                fontFamily: '"Fira Code", "Monaco", "Cascadia Code", "Roboto Mono", monospace',
                fontSize: 14,
                lineHeight: 1.5,
                minHeight: `${rows * 1.5 * 14 + 24}px`, // Approximate height based on rows
                backgroundColor: 'transparent',
                outline: 'none',
                resize: 'vertical',
              }}
              textareaClassName="code-editor-textarea"
              preClassName="code-editor-pre"
            />
          </Box>
          
          {(error || helperText) && (
            <FormHelperText 
              error={Boolean(error)}
              sx={{ mt: 0.5, mx: 1.5 }}
            >
              {error ? errorMessage : helperText}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
};
